import { useEffect, useMemo, useRef } from 'react'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import navigation from 'utils-support/navigation'
import * as Yup from 'yup'
import { AllProductWarrantyClaimFormFields, ProductWarrantyClaimForm, ProductWarrantyClaimFormValues } from '../components/Form'
import BasicFormLayout from '../components/FormLayout/BasicFormLayout'
import { useDispatch } from 'react-redux'
import { useReactToPrint } from 'react-to-print'
import { generateSubmissionError } from 'utils-support/Form/SubmissionError'
import { IProductWarrantyClaimDetails, ProductWarrantyClaimStatus } from '@cbgms/api'
import { ViewWarrantyPdfPageSetup } from '../common/ViewWarrantyPdfPageSetup'
import { useParams } from 'react-router-dom'
import { useGetProductWarrantyClaimDetails } from '../hooks/use-get-product-warranty-claim-details'
import { updateProductWarrantyClaim } from '../state/actions'
import { adhocAttachmentWholesalerUUID, parseBoolOrNull } from '../utils'
import { MediaCategories } from '@cbgms/api/modules/media/types'
import { processedFiles } from '../helpers/processedFiles'
import { postMediaItem } from 'app-support/media/state/request'

interface EditProductWarrantyClaimProps {
  onSubmit: (values: ProductWarrantyClaimFormValues) => Promise<ProductWarrantyClaimFormValues>
  onSubmitSuccess: (values: ProductWarrantyClaimFormValues) => void
}

export const EditProductWarrantyClaim = ({ onSubmit, onSubmitSuccess }: EditProductWarrantyClaimProps) => {
  const { t } = useTranslation()
  const printContentRef = useRef<HTMLDivElement>(null)
  const { warrantyUUID } = useParams<{ warrantyUUID: string }>()

  const { data, isLoading } = useGetProductWarrantyClaimDetails(warrantyUUID)

  const initialValues = useMemo(() => {
    if (!data) return null

    return {
      ...data,
      AttachmentsS3Internal: (data.AttachmentsS3Internal ?? []).map(item => ({
        Key: item.Key || undefined,
        Name: item.Name || undefined,
        ContentType: item.ContentType || undefined,
        File: item.File || undefined
      }))
    }
  }, [JSON.stringify(data)])

  const requiredMessage = t('global.field_is_required')

  const validationSchema = Yup.object().shape({
    Status: Yup.mixed<ProductWarrantyClaimStatus>().oneOf(Object.values(ProductWarrantyClaimStatus)).required(requiredMessage),
    ProcessedBy: Yup.string().required(requiredMessage),
    SupplierName: Yup.string().required(requiredMessage)
  })

  const handlePrint = useReactToPrint({
    contentRef: printContentRef
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault()
        handlePrint()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handlePrint])

  if (isLoading || !initialValues) return null

  return (
    <ProductWarrantyClaimForm
      initialValues={initialValues as IProductWarrantyClaimDetails}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      enableReinitialize
    >
      <BasicFormLayout
        backButtonProps={{ content: 'productWarrantyClaim.cancel' }}
        printButtonProps={{
          content: 'productWarrantyClaim.print_form',
          onClick: handlePrint
        }}
        title={initialValues.CaseNumber}
        submitButtonProps={{ children: 'productWarrantyClaim.save' }}
      >
        <AllProductWarrantyClaimFormFields printContentRef={printContentRef} />
        <ViewWarrantyPdfPageSetup />
      </BasicFormLayout>
    </ProductWarrantyClaimForm>
  )
}

const EditProductWarrantyClaimDefaultContainer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()

  const onSubmit = async (values: ProductWarrantyClaimFormValues) => {
    const payload = { ...values }
    payload.Assigned = parseBoolOrNull(values.Assigned?.toString())

    const validFiles: File[] =
      payload?.AttachmentsS3Internal?.filter(file => file.Name?.trim() && file.File instanceof File && file.File.size > 0).map(
        file => file.File as File
      ) || []

    let uploadedFiles: string[] = ['']
    if (validFiles?.length) {
      try {
        const data = await Promise.all(
          validFiles
            .map(file => ({
              companyUUID: payload.CompanyUUID,
              file,
              subCategory: adhocAttachmentWholesalerUUID,
              category: MediaCategories.AdhocAttachments
            }))
            .map(item => dispatch(postMediaItem(item)))
        )

        uploadedFiles = [...data.map(item => item.Data)].map(item => item.RelativePath)
      } catch (e) {
        return Promise.reject(false)
      }
    }

    payload.AttachmentsS3Internal = await processedFiles(payload?.AttachmentsS3Internal || [], uploadedFiles)

    try {
      const productWarrantyClaimUUID = (await dispatch(updateProductWarrantyClaim(payload)))?.Data?.UUID
      return { ...values, UUID: productWarrantyClaimUUID }
    } catch (error) {
      const apiError = generateSubmissionError(error)
      notifier.error(apiError.message)

      return Promise.reject(false)
    }
  }

  const onSubmitSuccess = () => {
    notifier.success(t('productWarrantyClaim.warranty_successfully_updated'))
    navigation.push('/warranty')
  }
  return <EditProductWarrantyClaim onSubmit={onSubmit} onSubmitSuccess={onSubmitSuccess} />
}

export default EditProductWarrantyClaimDefaultContainer
